/** What the guest form preview draws, as pure functions over a pre-check settings object,
 *  the operator's options and sample data — the guest app's (`bookingmgmt`) rules, each
 *  with its source:
 *
 *  - steps: Lead booker, one per party member (none when the party's details are not
 *    collected — `utils/booking.js getParticipantStepCount`), Pre-check-in, then Done;
 *  - a step's fields (`ParticipantStep.js`): the lead booker is never asked "Minor"; a party
 *    member's "Minor" comes first; "Custom note" always last; email and phone are hidden
 *    while "Minor" is ticked (`DynamicFields.js isFieldHidden`);
 *  - layout (`DynamicFields.js`): two columns from `lg`, filled left, right, left…; booleans,
 *    the custom note and long text span the row (long text: the planned guest-app P3 rule);
 *  - a question by its option count (`DynamicFields.js` thresholds);
 *  - waivers: those of the settings that the operator has enabled, in the operator's order
 *    (the guest login orders by `Waiver.sequence`, not by the list — plan F7);
 *  - the confirmation message: `{{ participant.… }}` filled from sample values
 *    (`apiv2/lib/waiver_renderer.py render_text_string`), split on newlines. */
import { asFields, isQuestion, PRECHECK_DEFAULTS, questionCode, type PreCheckField } from '../model';

import { GUEST } from './guestTokens';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** A pre-check settings object (`pre_check_settings`); missing keys read as the backend's
 *  defaults (`PRECHECK_DEFAULTS`). */
export type GuestPreviewSettings = Readonly<Record<string, unknown>>;

export interface GuestPreviewTag {
  id: number;
  name: string;
  friendly_name?: string | null;
}

/** A tag category a question asks, with what the guest login derives for it: required
 *  (min tags) and multiple choice (max tags ≠ 1), and its tags. */
export interface GuestPreviewCategory {
  code: string;
  name: string;
  required: boolean;
  multiselect: boolean;
  tags: readonly GuestPreviewTag[];
}

/** One of the operator's enabled waivers: its languages in their stored order and the
 *  text per language (HTML). */
export interface GuestPreviewWaiver {
  id: number;
  name: string;
  languages: readonly string[];
  html: Readonly<Record<string, string>>;
}

export interface GuestPreviewOptions {
  categories: readonly GuestPreviewCategory[];
  /** In the operator's order (`Waiver.sequence`) — the order guests see them. */
  waivers: readonly GuestPreviewWaiver[];
}

/** Sample data the preview shows — never a real guest. */
export interface GuestPreviewSample {
  operatorName: string;
  tourName: string;
  /** The trip's start as the guest app words it ("Jun 12, 2027 - 9:00 AM"). */
  startLabel: string;
  bookingRef: string;
  /** The lead booker, then the party members (the party size), with values by field id. */
  participants: ReadonlyArray<Readonly<Record<string, string | boolean>>>;
  /** Template variable values by path (`participant.user.first_name`). */
  variables: Readonly<Record<string, string>>;
}

export const SAMPLE_GUESTS: GuestPreviewSample = {
  operatorName: 'Sample Tours',
  tourName: 'Old Town Walking Tour',
  startLabel: 'Jun 12, 2027 - 9:00 AM',
  bookingRef: 'SMP-1042',
  participants: [
    { first_name: 'Alex', last_name: 'Morgan', email: 'alex.morgan@example.com' },
    { first_name: 'Sam', last_name: 'Morgan' },
  ],
  variables: {
    'participant.user.first_name': 'Alex',
    'participant.user.last_name': 'Morgan',
    'participant.user.email': 'alex.morgan@example.com',
    'participant.user.phone': '+1 555 0100',
    'participant.people_count': '2',
  },
};

/** The settings with the backend's defaults under them. */
export function withDefaults(settings: GuestPreviewSettings): Readonly<Record<string, unknown>> {
  return { ...PRECHECK_DEFAULTS, ...settings };
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/** `lead`, `guest-1` …, `checkin`, `done`. */
export type GuestPreviewStep = string;

export const LEAD_STEP = 'lead';
export const CHECKIN_STEP = 'checkin';
export const DONE_STEP = 'done';
export const guestStep = (n: number): GuestPreviewStep => `guest-${n}`;

export interface PreviewStepDef {
  id: GuestPreviewStep;
  /** The console's words for the step pill. */
  label: string;
  /** Participant steps: the index (0 = the lead booker). */
  participant: number | null;
}

/** The steps a guest walks through for these settings and this party. */
export function previewSteps(settings: GuestPreviewSettings, sample: GuestPreviewSample): PreviewStepDef[] {
  const s = withDefaults(settings);
  const party = s.collect_party_members_info === false ? 0 : Math.max(0, sample.participants.length - 1);
  return [
    { id: LEAD_STEP, label: 'Lead booker', participant: 0 },
    ...Array.from({ length: party }, (_, i) => ({ id: guestStep(i + 1), label: `Guest ${i + 1}`, participant: i + 1 })),
    { id: CHECKIN_STEP, label: 'Pre-check-in', participant: null },
    { id: DONE_STEP, label: 'Done', participant: null },
  ];
}

/** A step this form has (a guest step of a party that is not collected reads as the lead
 *  booker's). */
export function resolveStep(step: GuestPreviewStep, steps: readonly PreviewStepDef[]): PreviewStepDef {
  return steps.find((s) => s.id === step) ?? steps[0]!;
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

/** The fields a participant step asks, in the guest app's order. */
export function stepFields(settings: GuestPreviewSettings, participant: number): PreCheckField[] {
  const s = withDefaults(settings);
  const lead = participant === 0;
  let fields = asFields(lead ? s.lead_booker_fields : s.party_member_fields);
  if (lead) fields = fields.filter((f) => f.id !== 'is_minor');
  const minor = fields.find((f) => f.id === 'is_minor');
  if (minor) fields = [minor, ...fields.filter((f) => f !== minor)];
  const note = fields.find((f) => f.id === 'custom_note');
  if (note) fields = [...fields.filter((f) => f !== note), note];
  return fields;
}

/** Hidden while "Minor" is ticked. */
export function isHiddenForMinor(field: PreCheckField): boolean {
  return field.id === 'email' || field.id === 'phone';
}

/** Spans the row in the two-column layout. */
export function isFullWidth(field: PreCheckField): boolean {
  return field.type === 'boolean' || field.id === 'custom_note' || field.type === 'text';
}

export type FieldSection = { kind: 'columns'; left: PreCheckField[]; right: PreCheckField[] } | { kind: 'full'; field: PreCheckField };

/** The wide layout: runs of half-width fields in two columns (left, right, left …), a
 *  full-width field on a row of its own. */
export function fieldSections(fields: readonly PreCheckField[]): FieldSection[] {
  const out: FieldSection[] = [];
  let run: PreCheckField[] = [];
  const flush = () => {
    if (run.length === 0) return;
    out.push({ kind: 'columns', left: run.filter((_, i) => i % 2 === 0), right: run.filter((_, i) => i % 2 === 1) });
    run = [];
  };
  for (const f of fields) {
    if (isFullWidth(f)) {
      flush();
      out.push({ kind: 'full', field: f });
    } else run.push(f);
  }
  flush();
  return out;
}

export type FieldControl = 'text' | 'email' | 'phone' | 'number' | 'date' | 'checkbox' | 'multiline' | 'radio' | 'select' | 'checkboxes' | 'multiselect';

/** The control the guest app draws for a field (custom dates get a date picker — the
 *  planned guest-app P3 rule; the phone field is a plain text field here). */
export function fieldControl(field: PreCheckField, category: GuestPreviewCategory | undefined): FieldControl {
  if (isQuestion(field)) {
    const q = questionOf(field, category);
    if (q.multiselect) return q.tags.length >= GUEST.multiselectThreshold ? 'multiselect' : 'checkboxes';
    return q.tags.length >= GUEST.radioThreshold ? 'select' : 'radio';
  }
  if (field.type === 'boolean') return 'checkbox';
  if (field.type === 'number') return 'number';
  if (field.type === 'date' || field.id === 'date_of_birth') return 'date';
  if (field.id === 'phone') return 'phone';
  if (field.id === 'email') return 'email';
  if (field.id === 'custom_note' || field.type === 'text') return 'multiline';
  return 'text';
}

/** A question as the guest login sends it: the category's tags and derived flags, its
 *  name without a trailing colon. A category the operator no longer has asks nothing. */
export function questionOf(
  field: PreCheckField,
  category: GuestPreviewCategory | undefined,
): { label: string; required: boolean; multiselect: boolean; tags: readonly GuestPreviewTag[] } {
  const label = field.name.replace(/:$/, '');
  if (!category) return { label, required: field.required === true, multiselect: field.multiselect === true, tags: [] };
  return { label, required: category.required, multiselect: category.multiselect, tags: category.tags };
}

export function categoryFor(field: PreCheckField, options: GuestPreviewOptions): GuestPreviewCategory | undefined {
  const code = questionCode(field);
  return options.categories.find((c) => c.code === code);
}

/** The field's label as the guest sees it. */
export function fieldLabel(field: PreCheckField): string {
  return field.id === 'is_minor' ? 'This person is a minor' : field.name;
}

// ---------------------------------------------------------------------------
// Waivers and texts
// ---------------------------------------------------------------------------

/** The waivers guests sign: on, and among the operator's enabled waivers, in its order. */
export function shownWaivers(settings: GuestPreviewSettings, options: GuestPreviewOptions): GuestPreviewWaiver[] {
  const s = withDefaults(settings);
  if (s.waiver_enabled !== true || !Array.isArray(s.waivers)) return [];
  const ids = new Set((s.waivers as Array<{ waiver_id?: unknown }>).map((w) => Number(w?.waiver_id)));
  return options.waivers.filter((w) => ids.has(w.id));
}

/** Every language of these waivers, in first-seen order. */
export function waiverLanguages(waivers: readonly GuestPreviewWaiver[]): string[] {
  return [...new Set(waivers.flatMap((w) => w.languages))];
}

/** The language a waiver shows for the picked one: it, when the waiver has it; else the
 *  waiver's first (`WaiverList.js getEffectiveLang`). */
export function waiverLanguage(waiver: GuestPreviewWaiver, lang: string | null): string | null {
  return lang && waiver.languages.includes(lang) ? lang : (waiver.languages[0] ?? null);
}

const VARIABLE = /\{\{\s*([a-z0-9_.]+)\s*\}\}/g;
const ALLOWED_PATH = /^participant(\.[a-z0-9_]+)*$/;

/** `{{ participant.… }}` filled in like `render_text_string`: an allowed path takes its
 *  sample value (empty when the sample has none), anything else stays as typed. `escape`
 *  HTML-escapes the values (for waiver HTML). */
export function fillVariables(text: string, variables: Readonly<Record<string, string>>, escape = false): string {
  return text.replace(VARIABLE, (whole, path: string) => {
    if (!ALLOWED_PATH.test(path) || path.split('.').some((c) => c === '')) return whole;
    const v = variables[path] ?? '';
    return escape ? escapeHtml(v) : v;
  });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** The success screen's lines: the confirmation message (variables filled), else the
 *  guest app's default (`SuccessStep.js` — whose address is always "your registered
 *  email": it reads a field the party rows do not carry). */
export function confirmationLines(settings: GuestPreviewSettings, sample: GuestPreviewSample): string[] {
  const message = withDefaults(settings).confirmation_message;
  if (typeof message === 'string' && message !== '') return fillVariables(message, sample.variables).split('\n');
  return 'Confirmation email has been sent to your registered email.\nNo further action is required at this time.\n\nEnjoy your tour!'.split('\n');
}

/** The intro under "Pre-check-in process" (the guest app's default when empty). */
export function introText(settings: GuestPreviewSettings): string {
  const t = withDefaults(settings).intro_text;
  return typeof t === 'string' && t !== '' ? t : "Provide participating guests' contact information to pre-check-in.";
}

/** A participant's display name (`ParticipantsList.js`). */
export function participantName(values: Readonly<Record<string, unknown>> | undefined, index: number): string {
  const name = `${String(values?.first_name ?? '')} ${String(values?.last_name ?? '')}`.trim();
  return name || (index === 0 ? 'Lead Booker' : `Guest ${index}`);
}
